import { Link } from "@inertiajs/react";
import { CirclePlusIcon, EllipsisIcon } from "lucide-react";
import { PageTitle } from "@/components/page-title";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TableCellLink } from "@/components/table-cell-link";
import PaymentTransactionBadge from "@/components/payment-transaction-badge";
import InvoiceTypeBadge from "@/components/invoice-type-badge";
import { Pagination } from "@/components/ui/pagination";
import {
  PaymentTransaction,
  Invoice,
  PropsWithUser,
  Reconciliation,
} from "@/types";
import type PaginationType from "@/types/serializers/Pagination";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { formatDate } from "@/utils/format-date";
import { formatDatetime } from "@/utils/format-datetime";
import { formatMoney } from "@/utils/format-money";

type Props = {
  reconciliations: Reconciliation[];
  pagination: PaginationType;
};

function ReconciliationIndex({ reconciliations, pagination }: PropsWithUser<Props>) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <PageTitle>Reconciliations</PageTitle>
        <Button asChild>
          <Link href="/reconciliations/new">
            <CirclePlusIcon />
            New reconciliation
          </Link>
        </Button>
      </div>

      {reconciliations.map((reconciliation) => {
        return (
          <Card key={reconciliation.id}>
            <CardHeader>
              <div className="flex items-center justify-between gap-2">
                <CardTitle>
                  {formatDatetime(reconciliation.created_at)}
                </CardTitle>
                <Actions reconciliation={reconciliation} />
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h2 className="text-lg font-medium">Transactions</h2>
                  <PaymentTransactionsTable
                    payment_transactions={reconciliation.payment_transactions}
                  />
                </div>
                <div>
                  <h2 className="text-lg font-medium">Invoices</h2>
                  <InvoicesTable invoices={reconciliation.invoices} />
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}

      <Pagination pagination={pagination} />
    </div>
  );
}

function PaymentTransactionsTable({
  payment_transactions,
}: {
  payment_transactions: PaymentTransaction[];
}) {
  return (
    <div className="mt-4 overflow-hidden rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Type</TableHead>
            <TableHead>Date</TableHead>
            <TableHead>Merchant</TableHead>
            <TableHead>Reason</TableHead>
            <TableHead className="text-right">Amount</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {payment_transactions.map((payment_transaction) => {
            const href = `/payment_transactions/${payment_transaction.id}`;

            return (
              <TableRow
                key={payment_transaction.id}
                className="has-[[data-row-link]:focus-visible]:outline-ring/50 has-[[data-row-link]:focus-visible]:outline-2 has-[[data-row-link]:focus-visible]:-outline-offset-2"
              >
                <TableCell>
                  <TableCellLink href={href} tabIndex={-1} />
                  <PaymentTransactionBadge type={payment_transaction.type} />
                </TableCell>
                <TableCell>
                  <TableCellLink href={href} />
                  {formatDate(payment_transaction.date)}
                </TableCell>
                <TableCell>
                  <TableCellLink href={href} tabIndex={-1} />
                  {payment_transaction.counterparty.name}
                </TableCell>
                <TableCell
                  className="w-full max-w-0 truncate"
                  title={payment_transaction.reason}
                >
                  <TableCellLink href={href} tabIndex={-1} />
                  {payment_transaction.reason}
                </TableCell>
                <TableCell className="text-right font-mono font-semibold">
                  <TableCellLink href={href} tabIndex={-1} />
                  {payment_transaction.amount.amount}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

function InvoicesTable({ invoices }: { invoices: Invoice[] }) {
  return (
    <div className="mt-4 overflow-hidden rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Type</TableHead>
            <TableHead>Date</TableHead>
            <TableHead>Tax event date</TableHead>
            <TableHead>Merchant</TableHead>
            <TableHead>Number</TableHead>
            <TableHead className="text-right">Amount</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {invoices.map((invoice) => {
            const href = `/invoices/${invoice.id}`;

            return (
              <TableRow
                key={invoice.id}
                className="has-[[data-row-link]:focus-visible]:outline-ring/50 has-[[data-row-link]:focus-visible]:outline-2 has-[[data-row-link]:focus-visible]:-outline-offset-2"
              >
                <TableCell>
                  <TableCellLink href={href} tabIndex={-1} />
                  <InvoiceTypeBadge type={invoice.type} />
                </TableCell>
                <TableCell>
                  <TableCellLink href={href} />
                  {formatDate(invoice.issue_date)}
                </TableCell>
                <TableCell>
                  <TableCellLink href={href} />
                  {invoice.tax_event_date && formatDate(invoice.tax_event_date)}
                </TableCell>
                <TableCell>
                  <TableCellLink href={href} tabIndex={-1} />
                  {invoice.merchant.name}
                </TableCell>
                <TableCell className="tabular-nums">
                  <TableCellLink href={href} tabIndex={-1} />
                  {invoice.number}
                </TableCell>
                <TableCell className="text-right font-mono font-semibold">
                  <TableCellLink href={href} tabIndex={-1} />
                  {formatMoney(invoice.total_amount)}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

function Actions({ reconciliation }: { reconciliation: Reconciliation }) {
  return (
    <AlertDialog>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="size-8">
            <EllipsisIcon />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="bottom" align="end">
          <AlertDialogTrigger asChild>
            <DropdownMenuItem>Delete</DropdownMenuItem>
          </AlertDialogTrigger>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete reconciliation</AlertDialogTitle>
          <AlertDialogDescription>
            This action cannot be undone. This will permanently delete the
            reconciliation.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <Button variant="destructive" asChild>
            <Link
              href={`/reconciliations/${reconciliation.id}`}
              method="delete"
              preserveScroll
            >
              Delete
            </Link>
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default ReconciliationIndex;
